"use client";

// Figma-style coverflow of community work for the home pages: a prominent
// centered card with peeking neighbors, autoplay, and round prev/pause/next
// controls. Renders nothing until there are at least 3 published items so the
// homepage never looks broken at launch.

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";
import { canViewShowcase } from "@/lib/showcase/visibility";
import { useShowcaseFeed, type ShowcaseItem } from "@/hooks/useShowcaseApi";
import { resolveShowcaseTool } from "@/components/showcase/showcaseMeta";

const AUTOPLAY_MS = 5000;

export function LandingShowcaseCarousel({
  eyebrow = "Community Showcase",
  title = "Made in The Workshop",
  subtitle = "Real work from the community — liveries, badges, models, and more.",
  className,
}: {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const { isAdmin } = useAuth();
  const canView = canViewShowcase(isAdmin);
  const { data } = useShowcaseFeed(12, canView);
  const items = data?.items ?? [];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: items.length > 2, align: "center", containScroll: false });
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(true);
  const hoveringRef = useRef(false);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect).off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay — only when looping (3+ items); pauses on hover and via the button.
  useEffect(() => {
    if (!emblaApi || !playing || items.length < 3) return;
    const id = setInterval(() => {
      if (!hoveringRef.current) emblaApi.scrollNext();
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, playing, items.length]);

  if (!canView || items.length < 1) return null;

  const centered = items[selected] ?? items[0];
  const tool = resolveShowcaseTool(centered.source_tool);
  const ToolIcon = tool.icon;

  return (
    <section className={cn("relative overflow-hidden py-10 sm:py-12", className)}>
      <div className="lb-container">
        <div className="flex flex-col items-center gap-1.5 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{eyebrow}</p>
          <h2 className="text-lg font-semibold tracking-[-0.03em] text-foreground sm:text-xl">{title}</h2>
          <p className="max-w-md text-xs text-muted-foreground sm:text-sm">{subtitle}</p>
        </div>
      </div>

      {/* Carousel viewport — bounded to the page container so cards match the
          tool-card previews elsewhere on the page. */}
      <div className="lb-container mt-5 sm:mt-6">
        <div
          className="overflow-hidden"
          ref={emblaRef}
          onMouseEnter={() => (hoveringRef.current = true)}
          onMouseLeave={() => (hoveringRef.current = false)}
        >
          <div className="flex touch-pan-y">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="min-w-0 flex-[0_0_60%] px-2 sm:flex-[0_0_38%] lg:flex-[0_0_27%]"
              >
                <CarouselSlide item={item} active={index === selected} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Caption + controls. */}
      <div className="lb-container mt-5 flex flex-col items-center gap-3">
        <div className="flex min-h-[1.25rem] items-center gap-2 text-center text-xs text-muted-foreground sm:text-sm">
          <span className="font-semibold text-foreground">{centered.title}</span>
          <span aria-hidden>·</span>
          <ToolIcon className="h-3.5 w-3.5 text-accent" />
          <span>{tool.title}</span>
          <span aria-hidden>·</span>
          <span>by {centered.creator_handle}</span>
        </div>

        <div className="flex items-center gap-3">
          <RoundButton label="Previous" onClick={() => emblaApi?.scrollPrev()}>
            <ChevronLeft className="h-4 w-4" />
          </RoundButton>
          <RoundButton label={playing ? "Pause" : "Play"} onClick={() => setPlaying((p) => !p)}>
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </RoundButton>
          <RoundButton label="Next" onClick={() => emblaApi?.scrollNext()}>
            <ChevronRight className="h-4 w-4" />
          </RoundButton>
        </div>

        <Link
          href="/showcase"
          className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-foreground"
        >
          Browse the full showcase
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}

function CarouselSlide({ item, active }: { item: ShowcaseItem; active: boolean }) {
  return (
    <Link
      href={`/showcase/${item.id}`}
      className={cn(
        "block overflow-hidden rounded-2xl border bg-card shadow-sm transition-all duration-500 ease-out",
        active
          ? "scale-100 border-accent/40 opacity-100 shadow-md shadow-accent/10"
          : "scale-[0.86] border-border/60 opacity-50",
      )}
      tabIndex={active ? 0 : -1}
      aria-hidden={!active}
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
        {item.kind === "model" ? (
          <span className="absolute left-3 top-3 rounded-full border border-white/20 bg-black/55 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white/90">
            3D
          </span>
        ) : null}
      </div>
    </Link>
  );
}

function RoundButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-full border border-border/70 bg-card/80 text-foreground/80 transition hover:-translate-y-0.5 hover:border-accent/40 hover:bg-accent/5 hover:text-foreground"
    >
      {children}
    </button>
  );
}
